// libraries
import { v4 } from "uuid";
import { eachLimit } from "async";
import { doc, getDocFromServer } from "firebase/firestore";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

// services
import uploadService from "../../services/upload.service";
import requestService from "../../services/request.service";

// hooks
import { useDocSnap } from "../../hooks/useSubscription.v2";
import { useAsyncAction } from "../../hooks/useAsync";
import { useToastContext } from "../toast.context";
import { usePersistenceStorage } from "../../hooks/usePersistanceStorage";
import { useConnectivityStatus } from "../connectivity.context";
import { useAuthenticationContext } from "../authentication/authentication.context";

// utilities
import { swApi } from "../../service-workers/firebase-pre-cache/firebase-pre-cache.api";
import { timeout } from "wombat-global/src/utilities";
import { tryJSON } from "wombat-global/src/utilities/object.utilities";
import { resolveQueueItem } from "../../service-workers/firebase-pre-cache/firebase-pre-cache.utilities";
import {
  auth,
  generateFirestoreConnection,
} from "../../services/firebase.service";
import { precacheUsersOfflineDocuments } from "./precacheUsersOfflineDocuments";

// constants
import {
  ALL_OBJECTS,
  CURRENT_METADATA_DOCUMENT,
  DOCUMENTS_META_COLLECTION,
  GLOBAL_USERS_COLLECTION,
  INDEXED_COLLECTION,
  getAllRolesDocPaths,
  getDocumentPath,
  getSchemaPath,
  getTargetDocPath,
  targetPath,
} from "wombat-global/src/constants";
import { Time } from "wombat-global/src/utilities/time";

// types
import {
  IGlobalUserEntity,
  MetaDocumentsDocument,
  OfflineStorageConsent,
  AllObjectsReference,
  IClientTarget,
  ISiteTarget,
  ITenantTarget,
  UserReferenceEntity,
} from "wombat-global/src/typings";
import { precachePath } from "./precachePath";

export type OfflineRequestEvent = {
  key: string;
  type: string;
  eventName: "Save" | "Submit" | "Update";
  state: "pending" | "uploading" | "completed" | "errored";
  data: OfflineEvent;
  createdAt: number;
  updatedAt?: number;
};

export type OfflineSyncType = "forms" | "documents";
export type OfflineSyncStatus = "idle" | "in-progress" | "done" | "errored";
export type OfflineSyncProgress = {
  key: string;
  type: OfflineSyncType;
  total: number;
  completed: number;
  status: OfflineSyncStatus;
  syncedAt?: string;
};

const OFFLINE_TARGET_SYNC_CONCURRENCY = 2;

export function getOfflineSyncProgressId(
  key: string,
  type: OfflineSyncType,
): string {
  return `${key}:${type}`;
}

export function getOfflineSyncStampStorageKey(
  uid: string,
  key: string,
  type: OfflineSyncType,
): string {
  return `firebase-precache.${uid}.${key}.${type}`;
}

type OfflineSupportContextApi = {
  offline: boolean;
  offlineEvent: Record<string, OfflineRequestEvent>;
  offlineSyncProgress: Record<string, OfflineSyncProgress>;
  pushOfflineDataRequest: (
    key: string,
    requests: OfflineEvent["requests"],
  ) => void;
  upsertOfflineData: (
    key: string | undefined,
    eventName: OfflineRequestEvent["eventName"],
    type: OfflineRequestEvent["type"],
    data: OfflineEvent,
  ) => void;
  syncClientOffline: (
    path: string,
    consent: {
      target: IClientTarget | ISiteTarget;
      forms?: OfflineStorageConsent;
      documents?: OfflineStorageConsent;
    },
  ) => Promise<void>;
  getOfflineSyncStamp: (
    key: string,
    type: OfflineSyncType,
  ) => string | undefined;
};

type ScopedOfflineSupportContextApi = Omit<
  OfflineSupportContextApi,
  "getOfflineSyncStamp" | "offlineSyncProgress"
> & {
  offlineSyncProgress: Record<string, OfflineSyncProgress>;
  getOfflineSyncStamp: (key: string) => string | undefined;
};

const OfflineSupportContext = React.createContext<OfflineSupportContextApi>({
  offline: true,
  offlineEvent: {},
  offlineSyncProgress: {},
  upsertOfflineData: () => null,
  pushOfflineDataRequest: () => null,
  syncClientOffline: async () => {},
  getOfflineSyncStamp: () => undefined,
});

function getSyncTypeFromScope(
  scope?: OfflineSyncType,
): OfflineSyncType | undefined {
  if (scope === "documents") {
    return "documents";
  }

  if (scope === "forms") {
    return "forms";
  }
  return undefined;
}

function getScopedOfflineSyncProgress(
  offlineSyncProgress: Record<string, OfflineSyncProgress>,
  syncType: OfflineSyncType,
): Record<string, OfflineSyncProgress> {
  const scopedOfflineSyncProgress: Record<string, OfflineSyncProgress> = {};

  Object.values(offlineSyncProgress).forEach((progress) => {
    if (progress.type === syncType) {
      scopedOfflineSyncProgress[progress.key] = progress;
    }
  });

  return scopedOfflineSyncProgress;
}

async function getMetaDocumentDocument(
  target: IClientTarget | ISiteTarget,
): Promise<MetaDocumentsDocument | undefined> {
  const fb = await generateFirestoreConnection(target);

  return await getDocFromServer(
    doc(
      fb,
      `${targetPath(
        target,
      )}/${DOCUMENTS_META_COLLECTION}/${CURRENT_METADATA_DOCUMENT}`,
    ),
  ).then((d) => {
    return d.data() as MetaDocumentsDocument | undefined;
  });
}

async function getAllForms(
  target: IClientTarget | ISiteTarget,
): Promise<Record<string, AllObjectsReference> | undefined> {
  const fb = await generateFirestoreConnection(target);
  return await getDocFromServer(
    doc(fb, `${targetPath(target)}/${INDEXED_COLLECTION}/${ALL_OBJECTS}`),
  ).then((d) => {
    return d.data() as Record<string, AllObjectsReference> | undefined;
  });
}

type OfflineEvent = {
  state: {
    target: IClientTarget | ISiteTarget | ITenantTarget;
    formId: string;
    selectedUsers: Record<string, UserReferenceEntity>;
    // oxlint-disable-next-line typescript/no-explicit-any
    initialFormState: any;
  };
  requests: { endpoint: string; method: string; body: string }[];
};

async function syncOffline({
  key,
  type,
  uid,
  consent,
  target,
  onSyncProgress,
  onSyncComplete,
}: {
  key: string;
  type: OfflineSyncType;
  uid: string;
  consent: OfflineStorageConsent;
  target: IClientTarget | ISiteTarget;
  onSyncProgress?: (progress: OfflineSyncProgress) => void;
  onSyncComplete?: (progress: OfflineSyncProgress) => void;
}) {
  const syncedOn =
    localStorage.getItem(getOfflineSyncStampStorageKey(uid, key, type)) ||
    localStorage.getItem(`firebase-precache.${uid}.${key}`);
  const expiredSync =
    !syncedOn ||
    new Date().getTime() - new Date(syncedOn).getTime() > Time.days;

  /**
   * if user has consented to store org/tenant forms or documents continue
   */
  if (!consent || !consent.store || !expiredSync) {
    return;
  }

  /**
   * If documents or forms are precached, also cache target, and all Roles documents
   */

  if (type === "documents" || type === "forms") {
    await Promise.all([
      precachePath(getAllRolesDocPaths(target), {}, { mainThread: true }),
      precachePath(getTargetDocPath(target), {}, { mainThread: true }),
    ]);
  }

  /**
   * sync published documents
   */
  if (type === "documents") {
    const documentMeta = await getMetaDocumentDocument(target);
    const items = Object.entries(documentMeta?.data || {})
      /* Precache only if the item has been published */
      .filter(([, _doc]) => _doc.publishedAt)
      .map(([documentId, _doc]) => ({
        batch: key,
        path: getDocumentPath({ target, documentId }),
        meta: {
          publishedAt: _doc.publishedAt,
        },
      }));

    const total = items.length;
    let completed = 0;
    let hasError = false;

    onSyncProgress?.({
      key,
      type,
      total,
      completed,
      status: "in-progress",
    });

    await eachLimit(items, 3, async (item) => {
      await resolveQueueItem(
        item,
        () => null,
        () => {
          hasError = true;
        },
      );
      completed += 1;
      onSyncProgress?.({
        key,
        type,
        total,
        completed,
        status: "in-progress",
      });
    });

    const status: OfflineSyncStatus = hasError ? "errored" : "done";
    const finalProgress: OfflineSyncProgress = {
      key,
      type,
      total,
      completed,
      status,
      ...(status === "done" ? { syncedAt: new Date().toISOString() } : {}),
    };
    onSyncProgress?.(finalProgress);
    if (status === "done") {
      onSyncComplete?.(finalProgress);
    }
  }

  /**
   * sync published forms
   */
  if (type === "forms") {
    const allForms = await getAllForms(target);
    const items = Object.entries(allForms || {})
      /* Precache only if the item has been published */
      .filter(([, schema]) => schema.builtAt)
      .map(([schemaId, schema]) => ({
        batch: key,
        path: getSchemaPath({ target, schemaId }),
        meta: {
          builtAt: schema.builtAt,
        },
      }));

    const total = items.length;
    let completed = 0;
    let hasError = false;

    onSyncProgress?.({
      key,
      type,
      total,
      completed,
      status: "in-progress",
    });

    await eachLimit(items, 3, async (item) => {
      await resolveQueueItem(
        item,
        () => null,
        () => {
          hasError = true;
        },
      );
      completed += 1;
      onSyncProgress?.({
        key,
        type,
        total,
        completed,
        status: "in-progress",
      });
    });

    const status: OfflineSyncStatus = hasError ? "errored" : "done";
    const finalProgress: OfflineSyncProgress = {
      key,
      type,
      total,
      completed,
      status,
      ...(status === "done" ? { syncedAt: new Date().toISOString() } : {}),
    };
    onSyncProgress?.(finalProgress);
    if (status === "done") {
      onSyncComplete?.(finalProgress);
    }
  }
}

export const OfflineSupportProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const online = useConnectivityStatus();
  const { addToast } = useToastContext();
  const { user, uid } = useAuthenticationContext();
  const [offlineData, setOffline] = usePersistenceStorage<
    OfflineSupportContextApi["offlineEvent"]
  >("offline-form-storage", {}, {});
  const [offlineSyncProgress, setOfflineSyncProgress] = useState<
    Record<string, OfflineSyncProgress>
  >({});

  /**
   * This is really important, it fetches new data from the server for downstream getGlobalUserDocumentFromCache fetches.
   */
  const [globalUser] = useDocSnap<IGlobalUserEntity>(
    `${GLOBAL_USERS_COLLECTION}/${user?.uid}`,
    {
      enabled: Boolean(user?.uid),
    },
  );

  const upsertOfflineData = useCallback(
    (
      key: string | undefined,
      eventName: OfflineRequestEvent["eventName"],
      type: OfflineRequestEvent["type"],
      data: OfflineEvent,
    ) => {
      const _key = key || v4();
      setOffline((_d) => ({
        ..._d,
        [_key]: {
          ...(_d[_key] || {}),
          key: _key,
          type,
          data,
          eventName,
          updatedAt: Date.now(),
          ...(_d[_key] ? {} : { state: "pending", createdAt: Date.now() }),
        },
      }));
    },
    [setOffline],
  );

  const pushOfflineDataRequest = useCallback(
    (key: string, requests: OfflineEvent["requests"]) => {
      const _key = key || v4();
      setOffline((_d) => {
        if (!_d[key]) {
          // oxlint-disable-next-line
          console.warn("Offline data key DNE, Cannot add request");
          return _d;
        }

        return {
          ..._d,
          [_key]: {
            ..._d[_key],
            data: {
              ..._d[_key].data,
              requests: [..._d[_key].data.requests, ...requests],
            },
          },
        };
      });
    },
    [setOffline],
  );

  /**
   * @private
   * delayed removal of queue item
   */
  const removeEvent = useCallback(
    (key: string) => {
      setTimeout(() => {
        setOffline(({ [key]: data, ...d }) => d);
      }, 5 * Time.seconds);
    },
    [setOffline],
  );

  /**
   * @private
   * internal queue item state setter
   */
  const setEventState = useCallback(
    (key: string, state: OfflineRequestEvent["state"]) => {
      setOffline((d) => ({
        ...d,
        [key]: {
          ...d[key],
          state: state,
        },
      }));
    },
    [setOffline],
  );

  const syncWithCloud = useCallback(
    (keys?: string[]) => {
      return eachLimit(keys || Object.keys(offlineData ?? {}), 2, async (key) => {
        const req = (offlineData ?? {})[key];
        // this last loop must happen one request ofter the other (sequencing matters)
        await eachLimit(req.data.requests, 1, async (_req) => {
          /**
           * if offline event (form submission / creation) is active, don't send the request,
           * wait for page change
           */
          if (
            // form edit page ends with key
            window.location.pathname.endsWith(key) ||
            // form create page will have instanceId = key
            URL.parse(window.location.toString())?.searchParams.get(
              "instanceId",
            ) === key
          ) {
            // oxlint-disable-next-line no-console
            console.log("Pausing Offline submission, page is active", req);
            return;
          }

          try {
            const body = tryJSON(_req.body);

            await uploadService.enqueueAllFormFiles(key, body.data);
            await requestService.request(_req.endpoint, {
              body: JSON.stringify({ ...body, data: body?.data }),
              method: _req.method,
            });

            setEventState(key, "completed");
            removeEvent(key);
            return;
          } catch (err) {
            setEventState(key, "errored");
            throw err;
          }
        });
      });
    },
    [offlineData, setEventState, removeEvent],
  );

  /**
   * When app comes online, push up pending / errored requests
   */
  useAsyncAction({
    retry: 1,
    retryDelay: 20 * Time.seconds,
    refetchInterval: 20 * Time.hours,
    refetchIntervalInBackground: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    queryKey: ["offline-sync"],
    queryFn: async () => {
      // oxlint-disable-next-line no-console
      console.log("Offline query check", offlineData);
      if (online && Object.keys(offlineData ?? {}).length > 0) {
        await auth.authStateReady();
        // timeout so auth access token can be added to the request module
        await timeout(1000);
        // oxlint-disable-next-line
        console.log("Pushing saved request to cloud");
        syncWithCloud();
      }
      return null;
    },
    enabled: online,
  });

  /**
   * Register firebase service worker changes
   */
  useEffect(() => {
    const syncFlag: Record<string, string | undefined> = {};

    swApi.onStateChange(function swOnMessageHandler(state) {
      Object.entries(state.batches).forEach(async ([key, batch]) => {
        if (batch.status === "done" && !syncFlag[key]) {
          syncFlag[key] = "done";
          localStorage.setItem(
            `firebase-precache.${user?.uid}.${key}`,
            new Date().toISOString(),
          );
          addToast("Offline Data Synced");
        }
      });
    });
  }, [addToast, user?.uid]);

  /**
   * Whenever uid changes and theirs an internet connection. pre-cache offline documents
   */
  useAsyncAction({
    retry: 1,
    retryDelay: 20 * Time.seconds,
    refetchInterval: Time.hours,
    refetchIntervalInBackground: true,
    queryKey: ["precache-user-docs", uid],
    queryFn: async () => {
      if (online && uid) {
        // oxlint-disable-next-line no-console
        console.log("triggering precache", uid);
        await auth.authStateReady();
        await precacheUsersOfflineDocuments(uid);
      }
      return null;
    },
    enabled: Boolean(online && uid),
  });

  /**
   * Watch global user offlineStorage data, and sync when appropriate.
   * This requires that the user have an internet connection
   */
  useAsyncAction({
    retry: 1,
    retryDelay: 20 * Time.seconds,
    refetchInterval: Time.hours,
    refetchIntervalInBackground: true,
    queryKey: ["precache-client-documents", uid],
    queryFn: async () => {
      if (!uid) {
        return null;
      }

      await auth.authStateReady();
      const optedIntoCaching = globalUser?.offlineStorage_2 || {};

      await eachLimit(
        Object.entries(optedIntoCaching),
        OFFLINE_TARGET_SYNC_CONCURRENCY,
        async ([key, consent]) => {
          if (consent.forms) {
            await syncOffline({
              key,
              type: "forms",
              uid,
              consent: consent.forms,
              target: consent.target,
            });
          }

          if (consent.documents) {
            await syncOffline({
              key,
              type: "documents",
              uid,
              consent: consent.documents,
              target: consent.target,
            });
          }
        },
      );
      return null;
    },
    enabled: Boolean(globalUser && online),
  });

  /**
   * pre cache all of the clients forms / documents when user
   * has consented to.
   */
  const syncClientOffline = useCallback(
    async (
      path: string,
      consent: {
        target: IClientTarget | ISiteTarget;
        forms?: OfflineStorageConsent;
        documents?: OfflineStorageConsent;
      },
    ) => {
      if (!uid) {
        return;
      }
      const saveSyncedAt = (progress: OfflineSyncProgress) => {
        if (!progress.syncedAt) {
          return;
        }
        localStorage.setItem(
          getOfflineSyncStampStorageKey(uid, progress.key, progress.type),
          progress.syncedAt,
        );
        localStorage.setItem(
          `firebase-precache.${uid}.${progress.key}`,
          progress.syncedAt,
        );
      };
      const upsertSyncProgress = (progress: OfflineSyncProgress) => {
        const progressId = getOfflineSyncProgressId(
          progress.key,
          progress.type,
        );
        setOfflineSyncProgress((current) => ({
          ...current,
          [progressId]: progress,
        }));
      };

      if (consent.forms) {
        await syncOffline({
          key: path,
          type: "forms",
          uid: uid,
          consent: consent.forms,
          target: consent.target,
          onSyncProgress: upsertSyncProgress,
          onSyncComplete: saveSyncedAt,
        });
      }

      if (consent.documents) {
        await syncOffline({
          key: path,
          type: "documents",
          uid: uid,
          consent: consent.documents,
          target: consent.target,
          onSyncProgress: upsertSyncProgress,
          onSyncComplete: saveSyncedAt,
        });
      }
    },
    [uid],
  );

  const getOfflineSyncStamp = useCallback(
    (key: string, type: OfflineSyncType) => {
      if (!uid) {
        return undefined;
      }
      return (
        localStorage.getItem(getOfflineSyncStampStorageKey(uid, key, type)) ||
        localStorage.getItem(`firebase-precache.${uid}.${key}`) ||
        undefined
      );
    },
    [uid],
  );

  const api = useMemo<OfflineSupportContextApi>(
    () => ({
      offline: !online,
      offlineEvent: offlineData ?? {},
      offlineSyncProgress,
      pushOfflineDataRequest,
      upsertOfflineData,
      syncClientOffline,
      getOfflineSyncStamp,
    }),
    [
      online,
      offlineData,
      offlineSyncProgress,
      upsertOfflineData,
      pushOfflineDataRequest,
      syncClientOffline,
      getOfflineSyncStamp,
    ],
  );

  return (
    <OfflineSupportContext.Provider value={api}>
      {children}
    </OfflineSupportContext.Provider>
  );
};

export function useOfflineSupport(): OfflineSupportContextApi;
export function useOfflineSupport(
  scope: OfflineSyncType,
): ScopedOfflineSupportContextApi;
export function useOfflineSupport(
  scope?: OfflineSyncType,
): OfflineSupportContextApi | ScopedOfflineSupportContextApi {
  const store = useContext(OfflineSupportContext);
  const syncType = getSyncTypeFromScope(scope);

  return useMemo(() => {
    if (!syncType) {
      return store;
    }

    return {
      ...store,
      offlineSyncProgress: getScopedOfflineSyncProgress(
        store.offlineSyncProgress,
        syncType,
      ),
      getOfflineSyncStamp: (key: string) => {
        return store.getOfflineSyncStamp(key, syncType);
      },
    };
  }, [store, syncType]);
}
